/**
 * @description Helpers for the create-recurring-plan modal (local today + submit copy).
 * @dependencies formatCurrency (invoiceHelpers) for Charge $ labels
 * @notes Default start date must be the browser calendar day, not
 *   toISOString().slice(0, 10) — that is UTC and shows tomorrow after ~7pm US.
 *   Charge-now uses preview.isFirstChargeToday. Create/preview send viewerToday
 *   (this local calendar day) so a PST merchant on an EST company still charges
 *   on the start date they see. Cron stays company-local.
 *   startSchedulePreview ignores in-flight results after cleanup so a slower
 *   older preview cannot keep a stale Charge $ label.
 */

import { formatCurrency } from './invoiceHelpers';

/**
 * YYYY-MM-DD for the viewer's local calendar day.
 * @param {Date} [now]
 * @returns {string}
 */
export function localTodayYmd(now = new Date()) {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Footer label on the create-plan review step.
 * Same-day first charge uses Charge $x.xx so the merchant sees money will move.
 *
 * @param {object} opts
 * @param {boolean} opts.submitting
 * @param {boolean} opts.isFirstChargeToday
 * @param {number} opts.amountCents
 * @returns {string}
 */
export function createPlanSubmitLabel({
  submitting = false,
  isFirstChargeToday = false,
  amountCents = 0
} = {}) {
  const chargeNow = !!isFirstChargeToday && Number(amountCents) > 0;
  if (submitting) return chargeNow ? 'Charging...' : 'Creating...';
  if (chargeNow) return `Charge ${formatCurrency(Number(amountCents) / 100)}`;
  return 'Create Plan';
}

/**
 * Debounced preview fetch whose cleanup cancels the timer and drops in-flight
 * results. The HTTP call is not aborted; onResult/onError/onSettled must not
 * run after cancel so a slower older response cannot win.
 *
 * @param {object} opts
 * @param {() => Promise<object>} opts.fetchPreview
 * @param {(result: object) => void} opts.onResult
 * @param {(error: Error) => void} [opts.onError]
 * @param {() => void} [opts.onSettled]
 * @param {number} [opts.delayMs=250]
 * @returns {() => void} cleanup
 */
export function startSchedulePreview({
  fetchPreview,
  onResult,
  onError,
  onSettled,
  delayMs = 250
}) {
  let cancelled = false;
  const timer = setTimeout(async () => {
    try {
      const result = await fetchPreview();
      if (cancelled) return;
      onResult(result);
    } catch (error) {
      if (cancelled) return;
      if (onError) onError(error);
    } finally {
      if (!cancelled && onSettled) onSettled();
    }
  }, delayMs);
  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}
